import { useCallback, useContext, useEffect, useState } from 'react'

import type { OptimizelyFlagKey, OptimizelyFlagValue } from '@/types/optimizely-flags'

import { OPTIMIZELY_DEFAULT_WAIT_MS, OptimizelyContext } from '@/context/optimizelyContext'

export type UseOptimizelyFlagsInfoOptions = {
  waitForFlag?: OptimizelyFlagKey
  timeoutMs?: number
}

/**
 * Return type for useOptimizelyFlagsInfo. Defines the flag accessor API.
 */
export type UseOptimizelyFlagsInfo = {
  flags: Record<string, OptimizelyFlagValue> | undefined
  /** Assignments to report: excludes visitors held back by traffic allocation. */
  assignments: Record<string, OptimizelyFlagValue> | undefined
  getFlagValue: (key: OptimizelyFlagKey) => OptimizelyFlagValue | undefined
  isFlagReady: (key: OptimizelyFlagKey) => boolean
  isFlagVariant: (key: OptimizelyFlagKey, value: OptimizelyFlagValue) => boolean
  isOriginal: (key: OptimizelyFlagKey) => boolean
  isReadyToShow: boolean
  isConsentGiven: boolean
  setFlagOverride: (key: string, value: OptimizelyFlagValue) => void
}

/**
 * Returns Optimizely feature flag accessors. Must be used within OptimizelyProvider.
 *
 * isReadyToShow behaviour:
 *   - No consent: true immediately (user will see 'original', no experiment running)
 *   - Consent + flag arrives: true when flag is ready
 *   - Consent + timeout: true after timeoutMs (genuine failure — caller should report to Sentry)
 *
 * @see OptimizelyProvider in context/optimizelyContext.tsx
 */
export const useOptimizelyFlagsInfo = (options?: UseOptimizelyFlagsInfoOptions): UseOptimizelyFlagsInfo => {
  const context = useContext(OptimizelyContext)
  if (context === null) {
    throw new Error('useOptimizelyFlagsInfo must be used within an OptimizelyProvider')
  }

  const { flags, assignments, consentGiven, setFlagOverride } = context

  const [timedOut, setTimedOut] = useState(false)
  const waitForFlag = options?.waitForFlag
  const timeoutMs = options?.timeoutMs ?? OPTIMIZELY_DEFAULT_WAIT_MS

  const getFlagValue = useCallback(
    (key: OptimizelyFlagKey): OptimizelyFlagValue | undefined => {
      if (!flags) return undefined
      return flags[key]
    },
    [flags],
  )

  const isFlagReady = useCallback(
    (key: OptimizelyFlagKey): boolean => {
      return Boolean(flags && key in flags)
    },
    [flags],
  )

  const isFlagVariant = useCallback(
    (key: OptimizelyFlagKey, value: OptimizelyFlagValue): boolean => {
      return getFlagValue(key) === value
    },
    [getFlagValue],
  )

  const isOriginal = useCallback(
    (key: OptimizelyFlagKey): boolean => {
      return getFlagValue(key) === 'original'
    },
    [getFlagValue],
  )

  useEffect(() => {
    if (!waitForFlag) return
    if (!consentGiven) return // no consent → no experiment → no need to wait
    if (isFlagReady(waitForFlag)) return
    const timer = setTimeout(() => setTimedOut(true), timeoutMs)
    return () => clearTimeout(timer)
  }, [waitForFlag, timeoutMs, isFlagReady, consentGiven])

  const isReadyToShow = !waitForFlag || !consentGiven || isFlagReady(waitForFlag) || timedOut

  return {
    flags,
    assignments,
    getFlagValue,
    isFlagReady,
    isFlagVariant,
    isOriginal,
    isReadyToShow,
    isConsentGiven: consentGiven,
    setFlagOverride,
  }
}
